import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { type Editor } from '@tiptap/react';
import { Palette, RotateCcw, Check, X } from 'lucide-react';

interface ColorPickerPopoverProps {
  editor: Editor;
  className?: string;
}

const PRESET_BRAND_COLORS = [
  { name: 'SECERA Maroon', hex: '#722F38', bgClass: 'bg-[#722F38]' },
  { name: 'Muted Gray', hex: '#A1A1AA', bgClass: 'bg-[#A1A1AA]' },
];

export default function ColorPickerPopover({ editor, className = '' }: ColorPickerPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState('#722F38');
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Read current active color in editor
  const currentColor = (editor.getAttributes('textStyle').color || '').toUpperCase();
  const hasCustomColor = Boolean(currentColor);

  // Update position on open
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverWidth = 208; // w-52 = 13rem = 208px
      const left = Math.max(
        12,
        Math.min(window.innerWidth - popoverWidth - 12, rect.left + rect.width / 2 - popoverWidth / 2)
      );
      // If close to bottom of viewport, position above button instead
      const popoverHeight = 220;
      const showAbove = rect.bottom + popoverHeight > window.innerHeight && rect.top > popoverHeight;
      const top = showAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8;

      setCoords({ top, left });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const applyColor = (hex: string) => {
    if (!hex) return;
    const formatted = hex.startsWith('#') ? hex : `#${hex}`;
    editor.chain().focus().setColor(formatted).run();
    setHexInput(formatted);
  };

  const handleResetColor = () => {
    editor.chain().focus().unsetColor().run();
    setIsOpen(false);
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger Button in Bubble Menu */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1.5 rounded transition-all cursor-pointer flex items-center justify-center gap-1 shrink-0 ${
          hasCustomColor || isOpen
            ? 'bg-white/20 text-white shadow-xs'
            : 'text-white/80 hover:text-white hover:bg-white/10'
        }`}
        title="Pilih Warna Teks (Brand & Custom)"
      >
        <Palette className="w-3.5 h-3.5" />
        {hasCustomColor && (
          <span
            className="w-2 h-2 rounded-full border border-white/60 inline-block"
            style={{ backgroundColor: currentColor }}
          />
        )}
      </button>

      {/* Floating Color Picker Popover via React Portal to prevent overflow clipping */}
      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
            className="fixed z-[9999] w-52 bg-[#1C1817]/98 backdrop-blur-2xl border border-white/20 rounded-2xl p-3 shadow-2xl text-white animate-in fade-in zoom-in-95"
          >
            <div className="flex items-center justify-between pb-2 mb-2.5 border-b border-white/10">
              <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-white/70">
                Warna Teks
              </span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-white/50 hover:text-white p-0.5 rounded cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </div>

            {/* Core Brand Presets (Maroon & Muted Gray) */}
            <div className="mb-3">
              <span className="text-[9px] font-mono text-white/50 uppercase tracking-widest block mb-1.5">
                Warna Brand SECERA
              </span>
              <div className="flex items-center gap-2">
                {PRESET_BRAND_COLORS.map((color) => {
                  const isSelected = currentColor === color.hex.toUpperCase();
                  return (
                    <button
                      key={color.hex}
                      type="button"
                      onClick={() => {
                        if (isSelected) {
                          handleResetColor();
                        } else {
                          applyColor(color.hex);
                        }
                      }}
                      className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-xs font-sans transition-all cursor-pointer ${
                        isSelected
                          ? 'border-white bg-white/20 ring-1 ring-white/50 text-white font-medium'
                          : 'border-white/15 bg-white/5 hover:bg-white/10 text-white/80'
                      }`}
                      title={color.name}
                    >
                      <span className={`w-3 h-3 rounded-full ${color.bgClass} border border-white/40 shrink-0`} />
                      <span className="text-[11px] truncate">{color.name.replace('SECERA ', '')}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Color Selector (Color Wheel & Hex Input) */}
            <div className="pt-2 border-t border-white/10">
              <span className="text-[9px] font-mono text-white/50 uppercase tracking-widest block mb-1.5">
                Custom Color
              </span>
              <div className="flex items-center gap-2">
                {/* Native Color Wheel Input Wrapper */}
                <div className="relative w-8 h-8 rounded-lg overflow-hidden border border-white/30 shrink-0 group cursor-pointer shadow-inner">
                  <input
                    type="color"
                    value={currentColor || hexInput}
                    onChange={(e) => {
                      const newHex = e.target.value;
                      setHexInput(newHex);
                      applyColor(newHex);
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    title="Buka Color Wheel"
                  />
                  <div
                    className="w-full h-full"
                    style={{ backgroundColor: currentColor || hexInput }}
                  />
                </div>

                {/* Hex Input */}
                <div className="flex-1 flex items-center bg-black/40 border border-white/20 rounded-lg px-2 py-1 focus-within:border-white/60">
                  <input
                    type="text"
                    value={hexInput}
                    onChange={(e) => setHexInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyColor(hexInput);
                      }
                    }}
                    placeholder="#000000"
                    maxLength={7}
                    className="w-full bg-transparent text-white text-[11px] font-mono outline-none uppercase"
                  />
                  <button
                    type="button"
                    onClick={() => applyColor(hexInput)}
                    className="p-0.5 text-white/60 hover:text-white transition-colors cursor-pointer"
                    title="Terapkan Hex"
                  >
                    <Check className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>

            {/* Reset to Default Text Color */}
            {hasCustomColor && (
              <div className="mt-3 pt-2 border-t border-white/10 flex justify-end">
                <button
                  type="button"
                  onClick={handleResetColor}
                  className="flex items-center gap-1 text-[10px] text-white/60 hover:text-rose-400 font-mono transition-colors cursor-pointer"
                >
                  <RotateCcw className="w-2.5 h-2.5" />
                  <span>Reset ke Normal</span>
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
